import {
  createMemoDeriveForm,
  rulesetsSelector,
  allFormRulesetsSelector
} from "@icg360/rex";
import { createSelector } from "@reduxjs/toolkit";
import { keyBy, isEmpty } from "lodash";

import { QTC, PRODUCT } from "../../constants";
import { VAVE_CARRIER_NAME } from "../../components/quote-constants";
import { countRequiredEmptyPaymentFields } from "../../components/quote-transaction-component/utils";
import {
  commercialFusions,
  plFusions
} from "../../components/quote-transaction-component/fusions";
import { printDate } from "../../utils/dateParser";

import { hasFeatureFlag, pickQTC } from ".";

export const memoDeriveForm = {
  quote: createMemoDeriveForm(),
  checkout: createMemoDeriveForm(),
  newMortgagee: createMemoDeriveForm()
};

const noQuote = {};
export const selectQTCQuote = state =>
  state.quoteTransactionComponent?.quote || noQuote;

export const selectQTCSaving = state => state.quoteTransactionComponent?.saving;
export const selectQTCLoading = state =>
  state.quoteTransactionComponent?.loading;
export const selectQTCQuoteUtilityActiveTab = state =>
  state.quoteTransactionComponent?.quoteUtilityActiveTab;

const noInputs = {};
export const selectQuoteInputs = createSelector(
  selectQTCQuote,
  quote => quote.inputs || noInputs
);

export const selectAAL = createSelector(selectQuoteInputs, inputs => {
  if (inputs?.AnalysisInProcess && inputs?.AnalysisValidated) {
    const { AAL, AnalysisValidated, AnalysisInProcess } = inputs;
    return { hasAAL: true, AAL, AnalysisValidated, AnalysisInProcess };
  }
  return { hasAAL: false };
});

const noCarriers = [];
export const selectCarriers = createSelector(
  selectQTCQuote,
  quote => quote.carriers || noCarriers
);

export const selectBusinessName = createSelector(
  selectQuoteInputs,
  inputs => inputs.InsuredName || inputs.DBAName || ""
);

export const selectPolicyHolderName = createSelector(
  selectQuoteInputs,
  inputs =>
    inputs.InsuredName ||
    [inputs.InsuredFirstName, inputs.InsuredLastName]
      .filter(i => i)
      .join(" ") ||
    ""
);

export const selectExpirationDate = createSelector(
  selectQuoteInputs,
  selectCarriers,
  (inputs, carriers) => {
    // If we have a calculated expiration date from a carrier, use that.
    // Otherwise, use the value from inputs.
    if (
      carriers.length &&
      carriers[0].rateResults &&
      carriers[0].rateResults.ExpirationDate &&
      carriers[0].rateResults.ExpirationDate.result
    ) {
      return carriers[0].rateResults.ExpirationDate.result;
    }
    return inputs.ExpirationDate;
  }
);

export const selectReviewLock = createSelector(selectQTCQuote, ({ flags }) => {
  const reviewLock = flags?.ReviewLock;
  if (typeof reviewLock === "string") {
    return reviewLock.toLowerCase() === "true";
  }
  return reviewLock === true;
});

export const selectRCEData = createSelector(
  selectQuoteInputs,
  pickQTC,
  ({ CoverageA }, { replacementCost }) => ({
    hasError: replacementCost.error,
    replacementCostData: {
      CoverageA,
      ...replacementCost.replacementCostData
    },
    rceDetailedErrorHTML: replacementCost.rceDetailedErrorHTML
  })
);

export const selectRCEValidated = createSelector(
  selectQuoteInputs,
  ({ ReplacementCostValidated }) => {
    return (
      ReplacementCostValidated === undefined ||
      ReplacementCostValidated === "100"
    );
  }
);

export const selectRulesets = createSelector(
  rulesetsSelector,
  (rulesets = []) => rulesets.filter(set => set.result === "YES")
);

export const selectAllRulesetsWithIdAsIndex = createSelector(
  allFormRulesetsSelector,
  rulesets => keyBy(rulesets, rule => rule.idref)
);

export const selectIsCommercial = createSelector(
  selectQTCQuote,
  ({ productType }) => !!productType && productType.toLowerCase() === "bop"
);

export const selectIsQuoteEditHeaderEnabled = createSelector(
  [hasFeatureFlag("quote-edit-header")],
  hasQuoteEditHeader => hasQuoteEditHeader
);

export const selectIsQuoteUtilityEnabled = createSelector(
  [hasFeatureFlag("qtc-quote-utility"), selectIsCommercial],
  (hasQuoteUtility, isCommercial) => hasQuoteUtility && !isCommercial
);

const selectFusions = state => {
  const isCommercial = selectIsCommercial(state);
  return isCommercial ? commercialFusions : plFusions;
};

export const selectCcFormData = createSelector(
  pickQTC,
  quoteTransactionComponent => {
    return quoteTransactionComponent.ccFormData;
  }
);

export const selectAchFormData = createSelector(
  pickQTC,
  quoteTransactionComponent => {
    return quoteTransactionComponent.achFormData;
  }
);

export const selectCheckFormData = createSelector(
  pickQTC,
  quoteTransactionComponent => {
    return quoteTransactionComponent.checkFormData;
  }
);

const inputReducer = (accumulator, currentInput) => {
  if (currentInput.type === "group") {
    // If the type of the input is "group",
    // we need to pass it's "inputs" through this function again
    accumulator = currentInput.inputs.reduce(inputReducer, accumulator);
  } else if (currentInput.type !== "repeat") {
    // We ignore inputs with type "repeat" because they currently
    // do not have any "id" for which we could target a link
    // in the future, when the API changes, this should change
    accumulator.push(currentInput);
  }
  return accumulator;
};
export const selectAllInputsWithIdAsIndex = createSelector(
  state => {
    if (!state.rex.forms[QTC.QTC_FORM_ID]) return null;
    const { id, index } = state.rex.currentForm;
    const childId = state.rex.forms[id][index].name;
    return memoDeriveForm[childId](state, {
      fusions: selectFusions(state),
      useForm: { id: QTC.QTC_FORM_ID, childId }
    });
  },
  groups => {
    if (isEmpty(groups)) return [];

    const inputsArr = groups.reduce((accumulator, group) => {
      return group.inputs.reduce(inputReducer, accumulator);
    }, []);

    // translate array of inputs to object with "id" as keys
    const inputsWithIdAsIndex = keyBy(inputsArr, input => input.id);

    return inputsWithIdAsIndex;
  }
);

const createFormCompleteSelector = childFormId =>
  createSelector(
    state => {
      if (!state.rex.forms[QTC.QTC_FORM_ID]) return null;
      return memoDeriveForm[childFormId](state, {
        fusions: selectFusions(state),
        useForm: { id: QTC.QTC_FORM_ID, childId: childFormId }
      });
    },
    selectAchFormData,
    selectCcFormData,
    (groups, achFormData, ccFormData) => {
      if (!groups) return false;
      const incomplete = groups.some(group => {
        const count = countRequiredEmptyPaymentFields(
          group.inputs,
          achFormData,
          ccFormData
        );

        if (count > 0) return true;

        return group.inputs.some(input => {
          if (input.inputs) {
            return input.inputs.some(nestedGroup => {
              if (Array.isArray(nestedGroup))
                return nestedGroup.some(
                  nestedInput =>
                    nestedInput.isRequired || nestedInput.valid === false
                );
              return nestedGroup.isRequired || nestedGroup.valid === false;
            });
          }
          return input.isRequired || input.valid === false;
        });
      });
      return !incomplete;
    }
  );

export const selectIsQuoteFormComplete = createFormCompleteSelector(
  QTC.STEP_QUOTE
);
export const selectIsCheckoutFormComplete = createFormCompleteSelector(
  QTC.STEP_CHECKOUT
);

export const selectCurrentCarrier = createSelector(
  selectCarriers,
  carriers => carriers[0]
);

export const selectIsQuoteReferrable = createSelector(
  [selectCurrentCarrier, selectIsQuoteFormComplete],
  (currentCarrier, isQuoteFormComplete) => {
    if (!currentCarrier) return;
    const { eligibility } = currentCarrier;
    const isBlocked = eligibility?.block?.result;
    const isPrevented = eligibility?.prevent?.result;
    return isQuoteFormComplete && isBlocked && !isPrevented;
  }
);

export const selectIsQuoteEligible = createSelector(
  selectCurrentCarrier,
  currentCarrier =>
    !!currentCarrier &&
    !currentCarrier?.eligibility?.stop?.result &&
    !currentCarrier?.eligibility?.block?.result &&
    !currentCarrier?.eligibility?.prevent?.result
);

export const selectQTCModals = state => state.quoteTransactionComponent.modals;

export const selectQuoteLastSaved = createSelector(
  selectQTCSaving,
  ({ lastSaved }) => printDate(lastSaved, "M/d/yyyy h:mmaaa")
);
export const selectIsRatingImpacted = createSelector(
  selectQTCSaving,
  ({ isRatingImpacted }) => isRatingImpacted
);

export const selectHasPremium = createSelector(
  selectCurrentCarrier,
  carrier => !!carrier?.rateResults?.TotalPremium?.result
);

export const selectHasPriceGrid = createSelector(
  selectCurrentCarrier,
  carrier =>
    !!carrier?.rateResults?.OpPriceGridAvailableDeductibleOptions?.result
);

// Determines of loss history section / step should be shown
// is based on things like if it's BOP (commercial), or if the carrier is VAVE or
// an expanded markets product (which don't have loss history)
export const selectHasLossHistory = createSelector(
  selectIsCommercial,
  selectCurrentCarrier,
  selectQuoteInputs,
  (isCommercial, currentCarrier, quoteInputs) => {
    return (
      !isCommercial &&
      !(
        currentCarrier?.nameShort?.toUpperCase() === VAVE_CARRIER_NAME &&
        quoteInputs?.ExpandedMarketsIndicator === PRODUCT.Values.True
      )
    );
  }
);

export const selectLosses = createSelector(
  selectQTCQuote,
  ({ losses }) => losses || {}
);

// checks if there are pending referrals by checking the current task object
// in QuoteTransactionComponent
export const selectHasPendingReferrals = createSelector(
  pickQTC,
  ({ currentTask }) => {
    return !!(currentTask && currentTask?.notes?.length > 0);
  }
);

// unlocked:
// if complete is true, unlocked is true
// if complete is false, unlocked is true IF:
// - the form is complete (all required fields filled in)
// - the quote is eligible (no blocks)
// - the quote is not pending a referral
// - replacement cost validated is undefined or 100
export const selectLossHistoryUnlocked = createSelector(
  selectReviewLock,
  selectIsQuoteFormComplete,
  selectIsQuoteEligible,
  selectHasPendingReferrals,
  selectRCEValidated,
  (
    complete,
    isFormComplete,
    isQuoteEligible,
    hasPendingReferrals,
    replacementCostValidated
  ) => {
    return (
      complete ||
      (isFormComplete &&
        isQuoteEligible &&
        !hasPendingReferrals &&
        replacementCostValidated)
    );
  }
);

export const selectPropertyData = createSelector(
  selectQTCQuote,
  ({ property }) => property || {}
);

export const selectConsentToRate = createSelector(
  selectCurrentCarrier,
  carrier => carrier?.rateResults?.OpConsentToRate?.result === "100"
);

export const selectSearchMortgagee = state =>
  state.quoteTransactionComponent.searchMortgagee;

export const selectQTCViewTracker = createSelector(
  selectQuoteInputs,
  selectQTCQuote,
  selectIsCommercial,
  selectQuoteLastSaved,
  selectBusinessName,
  selectPolicyHolderName,
  selectCarriers,
  selectIsQuoteEligible,
  (
    inputs,
    quote,
    isCommercial,
    lastSaved,
    businessName,
    policyName,
    carriers,
    quoteEligibility
  ) => ({
    insuranceType: isCommercial ? "Commercial" : "Personal",
    productType: quote.productType || "",
    quoteCarriers: carriers.map(({ id }) => id),
    quoteLastSaved: lastSaved,
    quoteEffectiveDate: quote.effectiveDate,
    quoteEligibility,
    quoteId: quote.quoteId || null,
    quoteName: isCommercial ? businessName : policyName,
    quoteState: inputs.PropertyState || "",
    quoteZipCode: inputs.PropertyZipCode || ""
  })
);
